import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Users, Send, CheckCircle2, Database, RefreshCw, Ticket, TrendingUp, Sparkles, MapPin } from "lucide-react";
import CityPicker from "@/components/hola/CityPicker";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = [
  { id: "all", label: "Toda mi base", desc: "Todos los que escanearon tu QR", key: "total" },
  { id: "named", label: "Con nombre", desc: "Cupones 100% personalizados", key: "with_name" },
  { id: "never_redeemed", label: "Nunca han canjeado", desc: "Actívalos con una oferta fuerte", key: "never_redeemed" },
  { id: "redeemed", label: "Clientes fieles", desc: "Ya canjearon antes — prémialos", key: "redeemed_any" },
  { id: "city", label: "Toda la población", desc: "Todos los usuarios HOLA RADAR de una ciudad — aunque no sean tu base", key: "city", global: true },
];

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
const fmt = (s) => (s ? new Date(s).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function CampaignsPage() {
  return <DashboardShell active="campanas">{({ merchant }) => <Body merchant={merchant} />}</DashboardShell>;
}

function Body() {
  const [audience, setAudience] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ coupon_id: "", name: "", segment: "all", message: "Hola {nombre}, tenemos un cupón especial para ti. ¡Te esperamos!" });
  const [sending, setSending] = useState(false);
  const [city, setCity] = useState(null);
  const [redeem, setRedeem] = useState({ code: "", staff_name: "", ticket_amount: "" });
  const [redeeming, setRedeeming] = useState(false);
  const [lastRedeem, setLastRedeem] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    try {
      const [a, c, k, r] = await Promise.all([
        api.get("/merchants/me/audience"), api.get("/merchants/me/coupons"), api.get("/merchants/me/campaigns"), api.get("/merchants/me/coupons/report?days=90"),
      ]);
      setAudience(a.data); setCoupons(c.data.coupons.filter((x) => x.active && !x.expired)); setCampaigns(k.data.campaigns); setReport(r.data.stats);
      setForm((f) => ({ ...f, coupon_id: f.coupon_id || c.data.coupons.find((x) => x.active && !x.expired)?.coupon_id || "" }));
    } catch { toast.error("No pude cargar los datos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!form.coupon_id) return toast.error("Elige un cupón");
    const seg = SEGMENTS.find((s) => s.id === form.segment);
    if (seg.global && !city) return toast.error("Elige la población destino");
    const n = seg.global ? "todos los" : (audience?.stats?.[seg.key] ?? 0);
    const who = seg.global ? `usuarios HOLA RADAR de ${city.name}, ${city.state}` : `cliente${n === 1 ? "" : "s"} (${seg.label})`;
    if (!window.confirm(`Se enviará el cupón a ${n} ${who}. ¿Continuar?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/merchants/me/campaigns", { ...form, city_key: seg.global ? city.key : undefined });
      toast.success(`Campaña enviada a ${data.sent_count} clientes${data.skipped_already_had ? ` (${data.skipped_already_had} ya lo tenían)` : ""} · ${data.push_sent || 0} recibieron aviso push en su celular`);
      setForm((f) => ({ ...f, name: "" }));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "No pude enviar la campaña");
    } finally { setSending(false); }
  };

  const doRedeem = async (e) => {
    e.preventDefault();
    setRedeeming(true); setLastRedeem(null);
    try {
      const { data } = await api.post("/merchants/me/coupons/redeem", {
        code: redeem.code.trim().toUpperCase(), staff_name: redeem.staff_name.trim(),
        ticket_amount: redeem.ticket_amount === "" ? null : Number(redeem.ticket_amount),
      });
      setLastRedeem(data); setRedeem({ code: "", staff_name: redeem.staff_name, ticket_amount: "" });
      toast.success("Cupón canjeado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No pude canjear"); }
    finally { setRedeeming(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;
  const st = audience?.stats || {};
  const subs = audience?.subscribers || [];
  const selected = SEGMENTS.find((s) => s.id === form.segment);

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1"><Megaphone className="w-3 h-3" /> Marketing directo</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Mi base de datos & Campañas</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">Cada cliente que escanea tu QR personalizado entra aquí. Envíales cupones cuando quieras — llegan directo a su Cuponera, con su nombre si lo registraron. Y con <strong className="text-[#FF007A]">"Toda la población"</strong> tu cupón llega a todos los usuarios HOLA RADAR de tu ciudad, aunque nunca hayan escaneado tu QR.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Database} label="Clientes en mi base" value={st.total ?? 0} sub={`+${st.new_7d ?? 0} esta semana`} color="from-[#0055FF] to-[#3b7bff]" testid="camp-kpi-total" />
        <Kpi icon={Send} label="Cupones enviados (90d)" value={report?.sent ?? 0} sub={`${report?.viewed ?? 0} vistos`} color="from-[#FF007A] to-[#ff4d9e]" testid="camp-kpi-sent" />
        <Kpi icon={CheckCircle2} label="Canjeados" value={report?.redeemed ?? 0} sub={`${report?.conversion ?? 0}% conversión`} color="from-emerald-500 to-emerald-400" testid="camp-kpi-redeemed" />
        <Kpi icon={TrendingUp} label="Venta generada" value={money(report?.ticket_total)} sub={`${money(report?.discount_total)} en descuentos`} color="from-amber-500 to-orange-400" testid="camp-kpi-sales" />
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        {/* Crear campaña */}
        <form onSubmit={send} className="rounded-3xl border-2 border-[#0055FF]/30 bg-white p-6 space-y-5" data-testid="campaign-form">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0055FF]" /><h2 className="font-heading font-extrabold text-xl">Nueva campaña</h2></div>

          <div>
            <Label className="text-xs">1. ¿Qué cupón vas a enviar?</Label>
            {coupons.length === 0 ? (
              <div className="mt-1 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">No tienes cupones activos. <Link to="/dashboard/cupones" className="font-bold underline">Crea uno primero →</Link></div>
            ) : (
              <div className="mt-1 grid sm:grid-cols-2 gap-2">
                {coupons.map((c) => (
                  <button key={c.coupon_id} type="button" onClick={() => setForm((f) => ({ ...f, coupon_id: c.coupon_id }))} data-testid={`campaign-coupon-${c.coupon_id}`}
                    className={`flex items-center gap-3 rounded-2xl border-2 p-2.5 text-left transition-colors ${form.coupon_id === c.coupon_id ? "border-[#0055FF] bg-[#0055FF]/5" : "border-neutral-200 hover:border-neutral-300"}`}>
                    <div className="w-12 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-[#FF007A] to-[#0055FF] shrink-0">{c.image && <img src={c.image} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="min-w-0"><div className="font-heading font-extrabold text-sm leading-tight">{c.discount_label}</div><div className="text-xs text-neutral-600 truncate">{c.title}</div></div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">2. ¿A quién?</Label>
            <div className="mt-1 grid sm:grid-cols-2 gap-2">
              {SEGMENTS.map((s) => (
                <button key={s.id} type="button" onClick={() => setForm((f) => ({ ...f, segment: s.id }))} data-testid={`campaign-segment-${s.id}`}
                  className={`rounded-2xl border-2 p-3 text-left transition-colors ${form.segment === s.id ? "border-[#0055FF] bg-[#0055FF]/5" : "border-neutral-200 hover:border-neutral-300"}`}>
                  <div className="flex items-center justify-between"><span className="font-bold text-sm flex items-center gap-1">{s.global && <MapPin className="w-3.5 h-3.5 text-[#FF007A]" />}{s.label}</span>{s.global ? <span className="text-[9px] font-black uppercase tracking-widest rounded-full bg-[#FF007A] text-white px-2 py-0.5">Global</span> : <span className="font-heading font-black text-[#0055FF]">{st[s.key] ?? 0}</span>}</div>
                  <div className="text-[11px] text-neutral-500">{s.desc}</div>
                </button>
              ))}
            </div>
            {selected?.global && <div className="mt-3"><CityPicker value={city} onChange={setCity} /></div>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">3. Nombre interno de la campaña</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej. Martes flojo · Regreso a clases" className="mt-1 h-11 rounded-xl" required data-testid="campaign-name" />
            </div>
            <div>
              <Label className="text-xs">4. Mensaje personalizado</Label>
              <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="mt-1 rounded-xl min-h-[44px]" maxLength={300} data-testid="campaign-message" />
              <p className="text-[10px] text-neutral-500 mt-1">Usa <code className="bg-neutral-100 px-1 rounded">{"{nombre}"}</code> y se reemplaza por el nombre del cliente (si lo registró).</p>
            </div>
          </div>

          <Button type="submit" disabled={sending || !form.coupon_id} className="w-full h-12 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold text-base" data-testid="campaign-send">
            <Send className="w-4 h-4" /> {sending ? "Enviando…" : `Enviar a ${st[selected.key] ?? 0} cliente${(st[selected.key] ?? 0) === 1 ? "" : "s"}`}
          </Button>
        </form>

        {/* Canjear */}
        <div className="space-y-6">
          <form onSubmit={doRedeem} className="rounded-3xl border-2 border-[#FF007A]/30 bg-white p-5" data-testid="redeem-form">
            <div className="flex items-center gap-2 mb-1"><Ticket className="w-5 h-5 text-[#FF007A]" /><h2 className="font-heading font-extrabold text-lg">Canjear cupón en caja</h2></div>
            <p className="text-xs text-neutral-500 mb-3">El cliente te muestra su código CUP-XXXXXX. Escríbelo junto con el monto del ticket y el sistema calcula el descuento.</p>
            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mb-3">🛡️ Los canjes participan en el concurso <strong>CUPÓN DE ORO</strong> y se auditan (presencia GPS, ticket, repeticiones). Registra solo cupones realmente usados en una compra.</p>
            <div className="space-y-2">
              <Input value={redeem.code} onChange={(e) => setRedeem((r) => ({ ...r, code: e.target.value.toUpperCase() }))} placeholder="CUP-XXXXXX" className="h-11 rounded-xl font-mono uppercase tracking-widest" required data-testid="redeem-code" />
              <Input value={redeem.staff_name} onChange={(e) => setRedeem((r) => ({ ...r, staff_name: e.target.value }))} placeholder="Tu nombre (staff)" className="h-11 rounded-xl" required data-testid="redeem-staff" />
              <Input type="number" step="0.01" min={0} value={redeem.ticket_amount} onChange={(e) => setRedeem((r) => ({ ...r, ticket_amount: e.target.value }))} placeholder="Monto del ticket (MXN)" className="h-11 rounded-xl" data-testid="redeem-ticket" />
              <Button type="submit" disabled={redeeming} className="w-full h-11 rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold" data-testid="redeem-submit">{redeeming ? "…" : "Aplicar cupón"}</Button>
            </div>
            {lastRedeem && (
              <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900" data-testid="redeem-result">
                <div className="font-bold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {lastRedeem.customer_name} · {lastRedeem.coupon_title}</div>
                <div className="text-xs mt-1">Ticket {money(lastRedeem.ticket_amount)} · Descuento <strong>{money(lastRedeem.discount_amount)}</strong> ({lastRedeem.discount_label})</div>
                <div className="font-heading font-black text-2xl mt-1">Total a cobrar: {money(lastRedeem.total_after)}</div>
                <div className="text-[10px] mt-1 text-emerald-700/80">Canje auditado{lastRedeem.presence_verified ? " · presencia GPS verificada ✓" : ""}</div>
              </div>
            )}
          </form>

          {/* Tip marketing */}
          <div className="rounded-3xl bg-[#0A0A0A] text-white p-5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF007A]">Tip de marketing</div>
            <p className="text-sm mt-2 leading-relaxed text-white/85">
              Los cupones con <strong className="text-white">vigencia corta (3-7 días)</strong> convierten hasta 3× más. Combina un cupón de bienvenida permanente con campañas relámpago los días de menos venta.
              <br /><br />Cada campaña dispara un <strong className="text-white">aviso push</strong> al celular de los clientes que activaron notificaciones, y 2 días antes de vencer un cupón les recordamos automáticamente que lo usen.
            </p>
          </div>
        </div>
      </div>

      {/* Historial campañas */}
      <div className="rounded-3xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-extrabold text-lg">Historial de campañas</h2>
          <Button onClick={load} variant="outline" className="rounded-full h-9 text-xs"><RefreshCw className="w-3 h-3" /> Actualizar</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="py-2 font-bold">Fecha</th><th className="py-2 font-bold">Campaña</th><th className="py-2 font-bold">Cupón</th><th className="py-2 font-bold">Segmento</th>
              <th className="py-2 font-bold text-right">Enviados</th><th className="py-2 font-bold text-right">Push</th><th className="py-2 font-bold text-right">Vistos</th><th className="py-2 font-bold text-right">Canjeados</th><th className="py-2 font-bold text-right">Conv.</th><th className="py-2 font-bold text-right">Venta</th>
            </tr></thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-b border-neutral-100" data-testid={`campaign-row-${c.campaign_id}`}>
                  <td className="py-2 text-xs">{fmt(c.created_at)}</td><td className="py-2 font-bold">{c.name}</td><td className="py-2 text-xs">{c.coupon_title}</td>
                  <td className="py-2 text-xs">{c.segment === "city" ? <span className="inline-flex items-center gap-1 text-[#FF007A] font-bold"><MapPin className="w-3 h-3" />{c.city_name}</span> : SEGMENTS.find((s) => s.id === c.segment)?.label}</td>
                  <td className="py-2 text-right">{c.sent_count}</td><td className="py-2 text-right">{c.push_sent ?? 0}</td><td className="py-2 text-right">{c.viewed}</td><td className="py-2 text-right font-bold text-emerald-600">{c.redeemed}</td>
                  <td className="py-2 text-right">{c.conversion}%</td><td className="py-2 text-right">{money(c.ticket_total)}</td>
                </tr>
              ))}
              {campaigns.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-sm text-neutral-500">Aún no has lanzado campañas.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Base de datos */}
      <div className="rounded-3xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-[#0055FF]" /><h2 className="font-heading font-extrabold text-lg">Mi base de datos <span className="text-neutral-400 text-base font-semibold">({subs.length})</span></h2></div>
        <p className="text-xs text-neutral-500 mb-4">Clientes anónimos identificados por su celular. Los que registraron su nombre reciben cupones personalizados.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audience-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="py-2 font-bold">Cliente</th><th className="py-2 font-bold">Se unió</th><th className="py-2 font-bold text-right">Recibidos</th><th className="py-2 font-bold text-right">Canjeados</th><th className="py-2 font-bold text-right">Gastado</th>
            </tr></thead>
            <tbody>
              {(showAll ? subs : subs.slice(0, 15)).map((s) => (
                <tr key={s.device_id} className="border-b border-neutral-100">
                  <td className="py-2">{s.name ? <span className="font-bold">{s.name}</span> : <span className="text-neutral-400">Cliente anónimo · {s.device_id.slice(-6)}</span>}</td>
                  <td className="py-2 text-xs">{fmt(s.joined_at)}</td><td className="py-2 text-right">{s.received}</td><td className="py-2 text-right">{s.redeemed}</td><td className="py-2 text-right">{money(s.spent)}</td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-neutral-500">Todavía nadie ha escaneado tu QR personalizado. <Link to="/dashboard/cupones" className="font-bold text-[#0055FF] underline">Descárgalo aquí</Link>.</td></tr>}
            </tbody>
          </table>
          {subs.length > 15 && <button onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs font-bold text-[#0055FF]">{showAll ? "Ver menos" : `Ver todos (${subs.length})`}</button>}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${color} text-white p-4`} data-testid={testid}>
      <Icon className="w-4 h-4 opacity-80" />
      <div className="font-heading font-black text-2xl md:text-3xl leading-none mt-2">{value}</div>
      <div className="text-[11px] font-bold mt-1 leading-tight">{label}</div>
      <div className="text-[10px] text-white/80">{sub}</div>
    </div>
  );
}
